// File path: /shell/src/components/ModuleTabs.tsx
// Module Tabs - Horizontal tab bar for switching between modules

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ModuleId, ModuleDefinition, ModuleSubscription } from '@core/config/moduleRegistry';
import { hasModuleLoader } from '../context/ModuleContext';
import * as HeroIcons from '@heroicons/react/24/outline';

interface ModuleTabsProps {
  accessibleModules: Array<ModuleSubscription & { details?: ModuleDefinition }>;
  activeModule: ModuleId | null;
}

// Helper to get icon component by name
const getIconComponent = (iconName: string): React.ComponentType<{ className?: string }> => {
  const icons = HeroIcons as Record<string, React.ComponentType<{ className?: string }>>;
  const IconComponent = icons[`${iconName}Icon`];
  return IconComponent || HeroIcons.Squares2X2Icon;
};

const ModuleTabs: React.FC<ModuleTabsProps> = ({ accessibleModules, activeModule }) => {
  const location = useLocation();

  // Only non-core modules that can actually be opened: subscribed modules without a registered
  // loader (pathway, convergence, harmony) would dead-end
  const displayModules = accessibleModules.filter(
    m => m.details && !m.details.isCore && hasModuleLoader(m.moduleId)
  );

  // Sort by display order
  displayModules.sort((a, b) => (a.details?.displayOrder || 0) - (b.details?.displayOrder || 0));

  if (displayModules.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center h-12 space-x-1 overflow-x-auto">
      {displayModules.map((module) => {
        const isActive = activeModule === module.moduleId ||
          location.pathname.startsWith(`/${module.moduleId}`);

        const IconComponent = module.details?.icon
          ? getIconComponent(module.details.icon)
          : HeroIcons.Squares2X2Icon;

        return (
          <Link
            key={module.moduleId}
            to={`/${module.moduleId}`}
            className={`
              flex items-center px-4 py-2 rounded-lg text-sm font-medium
              transition-all duration-200 whitespace-nowrap
              ${isActive
                ? 'text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
            style={isActive ? { backgroundColor: module.details?.color || '#6b7280' } : undefined}
          >
            <IconComponent className={`h-5 w-5 mr-2 ${isActive ? 'text-white' : 'text-gray-400'}`} />
            <span>{module.details?.name || module.moduleName}</span>
            {module.tier !== 'basic' && (
              <span
                className={`
                  ml-2 px-1.5 py-0.5 text-xs rounded
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-500'
                  }
                `}
              >
                {module.tier === 'enterprise' ? 'ENT' : 'PRO'}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default ModuleTabs;
